import React from "react";
import { Button, Select, Slider, InputNumber } from "antd";
import {
  PlayCircleOutlined,
  LoadingOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

const DEFAULT_TREND_CONFIG = [
  { key: "solar_gen_33kv_mw", label: "Solar Gen at 33kV", color: "#f59e0b" },
  { key: "solar_to_bess_power", label: "Solar Power for BESS Charge", color: "#2563eb" },
  { key: "solar_power_at_poi", label: "Solar Power at POI", color: "#10b981" },
  { key: "grid_import_at_poi", label: "Grid Power at POI", color: "#ef4444" },
  { key: "bess_power_at_poi", label: "BESS Power at POI", color: "#8b5cf6" },
];

export default function StepRunSimulation({
  runningSim = false,
  handleRunSimulation,
  loadedFromHistory = false,
  simulationResults = null,
  selectedExportYear = "all",
  setSelectedExportYear,
  handleDownloadYearExcel,
  downloadingExcelYear = null,
  handleLoadHourlyDetails,
  selectedDetailsYear = null,
  selectedTrendFields = [
    "solar_gen_33kv_mw",
    "solar_to_bess_power",
    "solar_power_at_poi",
    "grid_import_at_poi",
    "bess_power_at_poi",
  ],
  setSelectedTrendFields,
  trendFieldConfig = DEFAULT_TREND_CONFIG,
  selectedChartDay = 1,
  setSelectedChartDay,
  loadingHourlyData = false,
  hourlyProfileData = [],
  onResetOrCreateNew,
  onBack,
}) {
  const activeDayData = React.useMemo(() => {
    if (!hourlyProfileData || hourlyProfileData.length === 0) return [];
    return hourlyProfileData.filter(
      (r) => Number(r.day) === Number(selectedChartDay)
    );
  }, [hourlyProfileData, selectedChartDay]);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="border border-slate-100 bg-slate-50/50 rounded-2xl p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h4 className="font-bold text-slate-800 text-base mb-1">
            Execute BESS Hybrid Simulation
          </h4>
          <p className="text-xs text-slate-500">
            Trigger the simulation model to compute hourly battery dispatch, solar export, and grid interactions.
          </p>
        </div>
        <button
          type="button"
          onClick={handleRunSimulation}
          disabled={runningSim}
          className="bess-btn-primary bg-emerald-600 hover:bg-emerald-700"
          style={{ backgroundColor: "#10b981", height: "48px", padding: "0 28px" }}
        >
          {runningSim ? <LoadingOutlined className="animate-spin" /> : <PlayCircleOutlined />}{" "}
          {loadedFromHistory ? "Re-run Simulation" : "Run Simulation"}
        </button>
      </div>

      {simulationResults && (
        <div className="space-y-8 animate-fade-in pt-4">
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <h3 className="font-bold text-slate-800 text-base uppercase tracking-wide">
                Yearly Summary Calculations
              </h3>
              {simulationResults?.yearly_summary && (
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-slate-500">Report Year:</span>
                  <Select
                    value={selectedExportYear}
                    onChange={(val) => setSelectedExportYear && setSelectedExportYear(val)}
                    style={{ width: 120 }}
                    size="small"
                    className="text-xs"
                    options={[
                      { value: "all", label: "All Years" },
                      ...simulationResults.yearly_summary.map((r) => ({
                        value: r.year,
                        label: `Year ${r.year}`,
                      })),
                    ]}
                  />
                  <Button
                    type="primary"
                    icon={
                      downloadingExcelYear === selectedExportYear ? (
                        <LoadingOutlined />
                      ) : (
                        <FileExcelOutlined />
                      )
                    }
                    onClick={() => handleDownloadYearExcel && handleDownloadYearExcel(selectedExportYear)}
                    loading={downloadingExcelYear === selectedExportYear}
                    style={{ backgroundColor: "#16a34a", borderColor: "#16a34a" }}
                    className="flex items-center text-xs font-semibold"
                  >
                    Download Excel{" "}
                    {selectedExportYear === "all" ? "(All Years)" : `(Year ${selectedExportYear})`}
                  </Button>
                </div>
              )}
            </div>
            <div
              className="overflow-auto border border-slate-100 rounded-xl bg-white shadow-sm"
              style={{ maxHeight: "600px" }}
            >
              <table className="bess-table">
                <thead>
                  <tr>
                    <th>Yr</th>
                    <th>Solar Gen (MWh)</th>
                    <th>Solar Exported (MWh)</th>
                    <th>Solar to BESS (MWh)</th>
                    <th>Grid Import (MWh)</th>
                    <th>BESS POI (MWh)</th>
                    <th>BESS Cap (MWh)</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {simulationResults.yearly_summary?.map((record) => (
                    <tr key={record.year} className="hover:bg-slate-50/30">
                      <td className="font-bold">Yr {record.year}</td>
                      <td>
                        {record.total_solar_gen?.toLocaleString(undefined, {
                          maximumFractionDigits: 2,
                        })}{" "}
                        MWh
                      </td>
                      <td>
                        {record.total_export_solar_power?.toLocaleString(undefined, {
                          maximumFractionDigits: 2,
                        })}{" "}
                        MWh
                      </td>
                      <td>
                        {record.total_solar_to_bess_power?.toLocaleString(undefined, {
                          maximumFractionDigits: 2,
                        })}{" "}
                        MWh
                      </td>
                      <td>
                        {record.total_grid_import?.toLocaleString(undefined, {
                          maximumFractionDigits: 2,
                        })}{" "}
                        MWh
                      </td>
                      <td>
                        {record.total_bess_discharge?.toLocaleString(undefined, {
                          maximumFractionDigits: 2,
                        })}{" "}
                        MWh
                      </td>
                      <td>
                        {record.bess_capacity?.toLocaleString(undefined, {
                          maximumFractionDigits: 2,
                        })}{" "}
                        MWh
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleLoadHourlyDetails && handleLoadHourlyDetails(record.year)}
                            className="px-3 py-1.5 bg-[#f0f9ff] text-[#0052CC] hover:bg-[#e0f2fe] border border-sky-100 rounded-lg font-semibold text-xs cursor-pointer transition-all shadow-sm"
                          >
                            Detailed Daily Profile
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDownloadYearExcel && handleDownloadYearExcel(record.year)}
                            disabled={downloadingExcelYear === record.year}
                            className="px-3 py-1.5 bg-[#f0fdf4] text-[#16a34a] hover:bg-[#dcfce7] border border-emerald-100 rounded-lg font-semibold text-xs cursor-pointer transition-all shadow-sm flex items-center gap-1"
                            title={`Download Excel report for Year ${record.year}`}
                          >
                            {downloadingExcelYear === record.year ? (
                              <LoadingOutlined />
                            ) : (
                              <FileExcelOutlined />
                            )}
                            <span>Excel (Yr {record.year})</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {selectedDetailsYear && (
            <div className="border border-slate-100 rounded-2xl p-6 bg-white shadow-sm space-y-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <h4 className="font-bold text-slate-800 text-sm">
                    Year {selectedDetailsYear} Hourly Generation & Dispatch Plots
                  </h4>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Select a day index (1-365) to render 24 hourly simulation values.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="min-w-[200px] max-w-[320px]">
                    <Select
                      mode="multiple"
                      allowClear
                      placeholder="Select fields to graph"
                      value={selectedTrendFields}
                      onChange={(vals) => setSelectedTrendFields && setSelectedTrendFields(vals)}
                      options={trendFieldConfig.map((f) => ({ label: f.label, value: f.key }))}
                      style={{ width: "100%" }}
                      maxTagCount="responsive"
                      size="small"
                    />
                  </div>
                  <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-100">
                    <span className="text-xs font-bold text-slate-500 uppercase">Day index:</span>
                    <InputNumber
                      min={1}
                      max={365}
                      value={selectedChartDay}
                      onChange={(val) => setSelectedChartDay && setSelectedChartDay(val || 1)}
                      className="w-16 h-8 text-xs rounded border-slate-200"
                    />
                  </div>
                </div>
              </div>

              <div className="w-full">
                <Slider
                  min={1}
                  max={365}
                  value={selectedChartDay}
                  onChange={(val) => setSelectedChartDay && setSelectedChartDay(val)}
                  tooltip={{ formatter: (v) => `Day ${v}` }}
                />
              </div>

              {loadingHourlyData ? (
                <div className="text-center py-20 text-slate-400 text-xs">
                  <LoadingOutlined className="animate-spin text-lg text-[#0052CC] mb-2" />
                  <div>Loading hourly generation curves...</div>
                </div>
              ) : activeDayData.length > 0 ? (
                <div className="h-80 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={activeDayData} margin={{ top: 10, right: 30, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis dataKey="hour" stroke="#94a3b8" fontSize={11} tickFormatter={(h) => `${h}:00`} />
                      <YAxis
                        stroke="#94a3b8"
                        fontSize={11}
                        label={{
                          value: "Power (MW)",
                          angle: -90,
                          position: "insideLeft",
                          fontSize: 11,
                        }}
                      />
                      <Tooltip
                        contentStyle={{
                          borderRadius: "12px",
                          border: "1px solid #f1f5f9",
                          boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.05)",
                        }}
                        labelFormatter={(h) => `Hour ${h}:00`}
                        formatter={(v, name) => [parseFloat(v).toFixed(2), name]}
                      />
                      <Legend verticalAlign="top" height={36} wrapperStyle={{ fontSize: 11 }} />
                      {trendFieldConfig
                        .filter((f) => selectedTrendFields.includes(f.key))
                        .map((f) => (
                          <Line
                            key={f.key}
                            type="monotone"
                            dataKey={f.key}
                            name={f.label}
                            stroke={f.color}
                            strokeWidth={2}
                            dot={false}
                          />
                        ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="text-center py-20 text-slate-400 text-xs italic">
                  No time-block data found for Day {selectedChartDay}.
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end pt-6 border-t border-slate-100">
            <button onClick={onResetOrCreateNew} className="bess-btn-primary">
              Create Another Simulation
            </button>
          </div>
        </div>
      )}

      {!simulationResults && (
        <div className="flex justify-between items-center pt-6 border-t border-slate-100 mt-8">
          <button type="button" onClick={onBack} className="bess-btn-secondary">
            Back
          </button>
        </div>
      )}
    </div>
  );
}
